import React, { useState, useMemo, useEffect } from 'react';
import { Typography, Box, Skeleton, Stack, Button, Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '../../components/PageTitle';
import { useAccountStats, AccountStats } from '../../hooks/api/useAccountStats';
import { useAuthContext } from '../../contexts/AuthContext';
import AddAccountDialog from './components/AddAccountDialog';
import EditAccountDialog from './components/EditAccountDialog';
import { StatisticsCardsContainer } from './components/StatisticsCardsContainer';
import { SearchField } from '../../components/inputs/SearchField';
import { AccountsTableDesktop } from './components/AccountsTableDesktop';
import { AccountsTableMobile } from './components/AccountsTableMobile';
import { ConfirmDisableDialog } from './components/ConfirmDisableDialog';
import { env } from '../../env';
import { AccountStatus, getAccountStatus } from './utils';

type SortableField =
  | '_id'
  | 'name'
  | 'status'
  | 'contract_expiration_date'
  | 'last_login'
  | 'active'
  | 'expired';

// Ascending puts active accounts first and disabled ones last.
const STATUS_ORDER: Record<AccountStatus, number> = {
  active: 0,
  trial: 1,
  disabled: 2,
};

const searchUrl = env.PUBLIC_SEARCH_URL;

const Accounts: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountStats, isLoading, disableAccount, refetch } =
    useAccountStats();
  const { loggedInAccount, setAccount } = useAuthContext();
  const [orderBy, setOrderBy] = useState<SortableField>('_id');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountStats | null>(
    null
  );
  const [expandedExtensions, setExpandedExtensions] = useState<Set<string>>(
    new Set()
  );
  const [loadingRows, setLoadingRows] = useState<Set<string>>(new Set());
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    accountId: string;
    accountName: string;
  }>({ open: false, accountId: '', accountName: '' });

  const handleSort = (property: SortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleDisableClick = (accountId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const account = accountStats.find((acc) => acc._id === accountId);
    setConfirmDialog({
      open: true,
      accountId,
      accountName: account?.name || accountId,
    });
  };

  const handleConfirmDisable = async () => {
    const { accountId } = confirmDialog;
    setConfirmDialog({ open: false, accountId: '', accountName: '' });

    // Add to loading state
    setLoadingRows((prev) => new Set(prev).add(accountId));

    try {
      await disableAccount(accountId);
    } catch (error) {
      console.error('Error disabling account:', error);
    } finally {
      // Remove from loading state
      setLoadingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleEdit = (account: AccountStats, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedAccount(account);
    setShowEditDialog(true);
  };

  const handleAgreementClick = (accountId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // Set the account context before navigating
    setAccount(accountId);
    navigate('/agreements');
  };

  const handleBrandingClick = (accountId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // Set the account context before navigating
    setAccount(accountId);
    navigate('/branding');
  };

  const toggleExtensionExpansion = (extensionKey: string) => {
    setExpandedExtensions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(extensionKey)) {
        newSet.delete(extensionKey);
      } else {
        newSet.add(extensionKey);
      }
      return newSet;
    });
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredAccountStats = useMemo(() => {
    return accountStats.filter((account) => {
      const searchLower = debouncedSearchTerm.toLowerCase();
      return (
        account._id.toLowerCase().includes(searchLower) ||
        (account.name && account.name.toLowerCase().includes(searchLower)) ||
        (account.extensions &&
          Object.entries(account.extensions).some(
            ([key, values]) =>
              key.toLowerCase().includes(searchLower) ||
              (Array.isArray(values) &&
                values.some((value) => value.toLowerCase().includes(searchLower)))
          ))
      );
    });
  }, [accountStats, debouncedSearchTerm]);

  const sortedAccountStats = useMemo(() => {
    return [...filteredAccountStats].sort((a, b) => {
      let aValue: string | number | undefined;
      let bValue: string | number | undefined;

      switch (orderBy) {
        case 'status':
          aValue = STATUS_ORDER[getAccountStatus(a)];
          bValue = STATUS_ORDER[getAccountStatus(b)];
          break;
        case 'active':
          aValue = a.agreements?.active?.agreements || 0;
          bValue = b.agreements?.active?.agreements || 0;
          break;
        case 'expired':
          aValue = a.agreements?.expired?.agreements || 0;
          bValue = b.agreements?.expired?.agreements || 0;
          break;
        default:
          aValue = a[orderBy] || '';
          bValue = b[orderBy] || '';
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [filteredAccountStats, orderBy, order]);

  return (
    <>
      <PageTitle title={t('accounts.title') as string} />
      <Box>
        <Card variant="default">
          <Stack spacing={24}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {t('accounts.title')}
                </Typography>
                <Typography variant="body1">
                  {t('accounts.description')}
                </Typography>
              </Box>
              {loggedInAccount === 'admin' && (
                <Button
                  variant="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddDialog(true)}
                >
                  {t('accounts.createAccount')}
                </Button>
              )}
            </Box>

            <StatisticsCardsContainer
              accountStats={accountStats}
              isLoading={isLoading}
            />

            <SearchField
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('accounts.searchPlaceholder') as string}
              ariaLabel={t('accounts.searchPlaceholder') as string}
            />
          </Stack>
        </Card>

        {isLoading ? (
          <Box pt={24}>
            <Skeleton variant="rectangular" height={400} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: { xs: 'none', lg: 'block' }, mt: 24 }}>
              <AccountsTableDesktop
                accounts={sortedAccountStats}
                orderBy={orderBy}
                order={order}
                onSort={handleSort}
                loggedInAccount={loggedInAccount}
                searchTerm={searchTerm}
                expandedExtensions={expandedExtensions}
                onToggleExtension={toggleExtensionExpansion}
                loadingRows={loadingRows}
                onDisableClick={handleDisableClick}
                onEdit={handleEdit}
                onAgreementClick={handleAgreementClick}
                onBrandingClick={handleBrandingClick}
                searchUrl={searchUrl}
              />
            </Box>
            <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 24 }}>
              <AccountsTableMobile
                accounts={sortedAccountStats}
                loggedInAccount={loggedInAccount}
                searchTerm={searchTerm}
                expandedExtensions={expandedExtensions}
                onToggleExtension={toggleExtensionExpansion}
                loadingRows={loadingRows}
                onDisableClick={handleDisableClick}
                onEdit={handleEdit}
                onAgreementClick={handleAgreementClick}
                onBrandingClick={handleBrandingClick}
                searchUrl={searchUrl}
              />
            </Box>
          </>
        )}
      </Box>
      <AddAccountDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={() => {
          refetch();
        }}
      />
      <EditAccountDialog
        open={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedAccount(null);
        }}
        account={selectedAccount}
        onSuccess={() => {
          refetch();
        }}
      />

      <ConfirmDisableDialog
        open={confirmDialog.open}
        accountName={confirmDialog.accountName}
        onConfirm={handleConfirmDisable}
        onClose={() =>
          setConfirmDialog({ open: false, accountId: '', accountName: '' })
        }
      />
    </>
  );
};

export default Accounts;
